import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { ProductModule } from '@reqtrack/shared'

interface ProductModuleEntry {
  module: string
  enabled: boolean
}

/**
 * Returns true if any product in the org has the given module enabled.
 * Fetches modules for all products in the org. Returns false until data loads.
 */
export function useOrgHasModule(
  orgId: string,
  productIds: string[],
  module: ProductModule
): boolean {
  const { data } = useQuery({
    queryKey: ['orgModules', orgId, module, productIds],
    queryFn: async () => {
      if (productIds.length === 0) return false
      const results = await Promise.all(
        productIds.map((pid) =>
          apiClient
            .get<{ data: ProductModuleEntry[] }>(`/products/${pid}/modules`)
            .then((r) => r.data.data)
            .catch(() => [] as ProductModuleEntry[])
        )
      )
      return results.some((mods) =>
        mods.some((m) => m.module === module && m.enabled)
      )
    },
    enabled: !!orgId && productIds.length > 0,
    staleTime: 60_000,
  })
  return data ?? false
}
